/**
 * Handles synchronization between local data and cloud data for the CloudNote application.
 *
 * This file completes the cloud sync process by:
 * 1. Fetching cloud data from remote storage
 * 2. Merging cloud data with local data using conflict resolution
 * 3. Updating the local store with merged data
 * 4. When changes are detected, performing cleanup and sync operations:
 *    - Saving merged data to IndexedDB persistent storage
 *    - Syncing changes back to the cloud
 *    - Removing deleted items (text files, tags, folders) from cloud storage
 *    - Uploading any missing text files to cloud storage
 *
 * The sync process ensures data consistency across devices while preserving user changes
 * through intelligent merge strategies.
 *
 * @module cloudSyncHandler
 */

import useAccountManagementStore, {
    userDataType,
} from "../stores/accountManagementStore";
import useDataStore from "../stores/dataStore";
import syncChangesToCloud from "./backend/syncChangesToCloud";
import getTableData from "./backend/table_database/getTableData";
import { saveDataStore } from "./cache/manageDataStore";
import { MergeResult, MergeTableData } from "./mergeTableData";
import removeDataFromCloud from "./removeDataFromCloud";
import { toast } from "react-toastify";

/**
 * Sync the local data with the cloud data.
 * If changes were made from the merge, save merged data to IndexedDB, sync changes to cloud, remove all deleted text files, tags, and folders from cloud, and upload missing text files to cloud.
 * @param {userDataType} userData - The user data to sync with the cloud.
 * @returns {Promise<any>} - Promise that resolves with the result of the sync operation.
 */
export default function cloudSyncHandler(userData?: userDataType) {
    const userDataTemp =
        userData || useAccountManagementStore.getState().userData;

    return getTableData().then((cloudTableData) => {
        // Check if the session is invalid
        if (cloudTableData === "INVALID_SESSION") {
            console.log("Session is invalid. Logging out user.");
            // Trigger logout flow
            useAccountManagementStore.getState().setLoginState("default");
            useAccountManagementStore.getState().setUserData(null);
            localStorage.removeItem("userAccount");
            return;
        }

        if (!cloudTableData) return;
        // Get local data
        const localTableData = useDataStore.getState().data;

        // Merge cloud and local data safely
        const mergeResult = MergeTableData(localTableData, cloudTableData);

        // Update the store with merged data
        useDataStore.getState().set.data(() => mergeResult.data);

        // If changes were made from the merge
        const changesMade = checkChangesMade(mergeResult);
        const cloudChangesMade = checkCloudChangesMade(mergeResult);
        printWereChangesMade(changesMade, cloudChangesMade);

        if (changesMade) {
            // Save merged data to IndexedDB
            saveDataStore(userDataTemp.id, true);
            // Upsert changes (and text files) to cloud
            syncChangesToCloud(mergeResult, true);
            // Remove all deleted text files, tags, and folders from cloud
            removeDataFromCloud(mergeResult["removeFromCloud"]);
        }
        // Still need to upload if cloud data is newer
        else if (cloudChangesMade) {
            saveDataStore(userDataTemp.id, true);
        }
    });
}

function checkChangesMade(mergeResult: MergeResult): boolean {
    const { conflicts, removeFromCloud } = mergeResult;
    const { localNewer } = conflicts;

    return (
        Object.values(localNewer?.notes || {}).length > 0 ||
        Object.values(localNewer?.folders || {}).length > 0 ||
        Object.values(localNewer?.tags || {}).length > 0 ||
        Object.values(localNewer?.deleted?.folders || {}).length > 0 ||
        Object.values(localNewer?.deleted?.notes || {}).length > 0 ||
        Object.values(localNewer?.deleted?.tags || {}).length > 0 ||
        removeFromCloud.folders.length > 0 ||
        removeFromCloud.notes.length > 0 ||
        removeFromCloud.tags.length > 0
    );
}

function checkCloudChangesMade(mergeResult: MergeResult): boolean {
    const { conflicts } = mergeResult;
    const { cloudNewer } = conflicts;

    return (
        Object.values(cloudNewer?.notes || {}).length > 0 ||
        Object.values(cloudNewer?.folders || {}).length > 0 ||
        Object.values(cloudNewer?.tags || {}).length > 0
    );
}

async function printWereChangesMade(
    localChangesMade: boolean,
    cloudChangesMade: boolean
) {
    if (localChangesMade && cloudChangesMade) {
        console.log("Both local and cloud changes were made.");
        toast.info("Local and Cloud changes were made.");
    } else if (localChangesMade) {
        console.log("Only local changes were made.");
        toast.info("Local changes were made.");
    } else if (cloudChangesMade) {
        console.log("Only cloud changes were made.");
        toast.info("Cloud changes were made.");
    } else {
        console.log("No changes were made.");
        // toast.info("No changes were made.");
    }
}
